import { StockUtils } from '@frame/StockUtils'
import { zodResolver } from '@hookform/resolvers/zod'
import useCartItems from '@order/hooks/useCart'
import { useOrderState } from '@order/hooks/useOrderState'
import { OrderItem } from '@order/orderTypes'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { StockFormUtils } from './StockFormUtils'
import { useConfigConditionFrameCheck } from 'features/config/useConfigCondition'
import useOrderValues from '@order/hooks/values/useOrderValues'

const useStockForm = (item: OrderItem) => {
  const config = useRemoteConfig()
  const cart = useCartItems()
  const order = useOrderState((s) => s.order)
  const values = useOrderValues({})
  const check = useConfigConditionFrameCheck({
    values,
  })

  const showTempleStock = useMemo(
    () => !!item.frame && !!check('frame:showTempleStock', item.frame),
    [check, item.frame]
  )

  // Create default values for the form based on current stock state, item other cart items
  const sources = useMemo(
    () =>
      StockFormUtils.getAvailableSources({
        item,
        order,
        config,
        stock: order.stock,
        cartItems: cart.list,
      }),
    [item, order, config, cart.list]
  )
  const defaultValues = useMemo(() => {
    const frameValues = StockFormUtils.getFormValues(
      item.frame?.default_code,
      order.stock[StockUtils.getItemKey(item.id, 'frame')],
      sources.frame?.available || []
    )
    const templeValues = StockFormUtils.getFormValues(
      item.temple?.name,
      order.stock[StockUtils.getItemKey(item.id, 'temple')],
      sources.temple?.available || []
    )
    return {
      frame: frameValues,
      temple: templeValues,
    }
  }, [order, item, sources])

  // Create the form with the schema and default values
  const form = useForm({
    defaultValues,
    reValidateMode: 'onChange',
    resolver: zodResolver(
      StockFormUtils.createStockFormSchema(item, showTempleStock)
    ),
  })

  return {
    form,
    sources,
    showTempleStock,
  }
}

export type StockForm = ReturnType<typeof useStockForm>['form']

export default useStockForm
